import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Footer from '@/components/pages/shared/Footer';
import HeadSEO from '@/components/seo/HeadSEO';

const TermsAndConditions: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const sections = t('termsAndConditions.sections', {
    returnObjects: true,
  }) as { titulo: string; texto?: string; items?: string[] }[];

  return (
    <div className="flex flex-col min-h-screen bg-white dark:bg-black text-gray-800 dark:text-gray-100">
      {/* ── SEO ───────────────────────────────────────────────────────────── */}
      {/* noIndex cambiado a false — Google la rastreó y no la indexó porque
          tenía noIndex=true. Los términos legales SÍ deben indexarse para
          dar señales de confianza (E-E-A-T) y para que Google entienda
          que es un sitio legítimo con política legal clara. */}
      <HeadSEO
        title="Términos y condiciones — Bookoll"
        description="Lee los términos y condiciones de uso de Bookoll. Información sobre el uso de la plataforma, derechos y obligaciones de usuarios y empresas registradas."
        canonical="https://bookoll.com/terms"
        ogImage="https://bookoll.com/og-home.jpg"
        noIndex={false}
      />

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden text-white w-full">
        {/* Fondo modo claro */}
        <div
          className="absolute inset-0 dark:hidden"
          style={{
            background: `
              radial-gradient(ellipse at 15% 60%, rgba(40,142,127,0.22) 0%, transparent 50%),
              radial-gradient(ellipse at 85% 20%, rgba(16,50,84,0.5) 0%, transparent 55%),
              linear-gradient(170deg, #103254 0%, #103254 25%, #1a4a6e 45%, #3d7fa0 68%, #a8cfe0 84%, #e8f4f8 95%, #ffffff 100%)
            `,
          }}
        />
        {/* Fondo modo oscuro: negro con resplandor teal, funde con el resto de la app */}
        <div
          className="absolute inset-0 hidden dark:block"
          style={{
            background: `
              radial-gradient(ellipse at 15% 55%, rgba(40,142,127,0.35) 0%, transparent 55%),
              radial-gradient(ellipse at 85% 15%, rgba(16,50,84,0.45) 0%, transparent 55%),
              linear-gradient(170deg, #050807 0%, #0a1a16 22%, #123a30 42%, #103254 60%, #0a0f14 82%, #000000 100%)
            `,
          }}
        />

        <motion.div
          className="absolute top-10 left-[5%] w-56 h-56 rounded-full pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(40,142,127,0.18) 0%, transparent 70%)',
            filter: 'blur(40px)',
          }}
          animate={{ scale: [1, 1.1, 1], opacity: [0.5, 0.9, 0.5] }}
          transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-4 right-[8%] w-64 h-64 rounded-full pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(255,255,255,0.07) 0%, transparent 70%)',
            filter: 'blur(50px)',
          }}
          animate={{ scale: [1, 1.08, 1], opacity: [0.4, 0.75, 0.4] }}
          transition={{
            duration: 9,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 3,
          }}
        />

        <button
          onClick={() => navigate(-1)}
          aria-label="Volver atrás"
          className="absolute top-5 left-5 z-20 flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 text-sm text-white hover:bg-white/20 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline font-medium">Volver</span>
        </button>

        <div className="relative z-10 pt-20 pb-28 px-6 flex flex-col items-center text-center max-w-3xl mx-auto">
          <motion.div
            className="mb-5 w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center shadow-lg"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <FileText className="w-7 h-7 text-white" />
          </motion.div>

          <motion.h1
            className="text-4xl md:text-5xl font-bold tracking-tight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut', delay: 0.1 }}
          >
            {t('termsAndConditions.title')}
          </motion.h1>

          <motion.div
            className="mt-4 h-px w-16 mx-auto"
            style={{
              background:
                'linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)',
            }}
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.7, delay: 0.5 }}
          />

          <motion.p
            className="mt-4 text-base text-white/70 max-w-xl leading-relaxed"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut', delay: 0.3 }}
          >
            {t('termsAndConditions.subtitle').split('Bookoll')[0]}
            <span className="font-semibold text-white">Bookoll</span>
            {t('termsAndConditions.subtitle').split('Bookoll')[1]}
          </motion.p>
        </div>

        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none pointer-events-none">
          <svg
            viewBox="0 0 1440 60"
            preserveAspectRatio="none"
            className="w-full h-10 sm:h-14"
          >
            <path
              d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z"
              className="fill-white dark:fill-black"
            />
          </svg>
        </div>
      </section>

      {/* ── Contenido ─────────────────────────────────────────────────────── */}
      <section className="py-16 w-full flex-1 bg-white dark:bg-black">
        <div className="max-w-3xl mx-auto px-6 space-y-0">
          {sections.map((section, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.4, ease: 'easeOut', delay: i * 0.04 }}
              className="group relative py-8 border-b border-gray-100 dark:border-white/10 last:border-0"
            >
              <div className="flex items-start gap-5">
                <span className="shrink-0 mt-0.5 w-8 h-8 rounded-lg bg-[#103254]/6 dark:bg-white/10 flex items-center justify-center text-xs font-bold text-[#103254]/40 dark:text-white/40 select-none">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div>
                  <h2 className="text-lg font-semibold text-[#103254] dark:text-white mb-2 group-hover:text-[#1a4a6e] dark:group-hover:text-white/80 transition-colors">
                    {section.titulo}
                  </h2>
                  <div className="text-gray-500 dark:text-gray-400 leading-relaxed text-sm space-y-2">
                    {section.texto && <p>{section.texto}</p>}
                    {section.items && (
                      <ul className="list-disc pl-5 space-y-1">
                        {section.items.map((item, k) => (
                          <li key={k}>{item}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}

          <div className="pt-12 pb-4 text-center">
            <span className="inline-block text-xs text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 rounded-full px-4 py-1.5">
              {t('termsAndConditions.lastUpdate')}
            </span>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsAndConditions;